import { useEffect, useMemo, useState } from 'react';
import {
  Surface,
  cardStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import {
  agentOperatorsRepoFor,
  agentEarningsLedgerRepoFor,
  agentsRepo,
  subscribeAgentsForBrand,
} from '../lib/repositories/agents-repo.js';
import { callProcessOutletRemovalsNow } from '../lib/callables/billing.js';
import { KycScanLink } from '../components/kyc-scan-link.js';
import { logger } from '../lib/logger.js';
import {
  money,
  paidOutSen,
  payableBalanceSen,
  type AgentOperatorBackref,
  type AgentProfile,
  type AgentStatus,
  type AgentTier,
  type EarningsLedgerEntry,
} from '@kilat/shared';

/**
 * /superadmin/agents — platform super-admin moderation.
 *
 * Access: gated to users whose `claims.superadmin[currentBrandId]` is true
 * (ADR-0017 — super-admin is scoped per platform brand). Other users see a
 * polite "not authorised here" screen with a link back to /queue so they
 * never end up in a dead-end.
 *
 * Capabilities (Sprint 19 MVP):
 *   - Live list of all agents on the current brand. Search by name / code /
 *     email; filter by status.
 *   - Detail panel showing the full profile (read-only) + back-references
 *     (operators they brought in) + a slim earnings-ledger summary.
 *   - Per-agent controls: status (active / suspended / terminated / pending_kyc /
 *     applied), tier (bronze / silver / gold). claimsSyncAgent propagates
 *     status changes through the revocation funnel (G2/ADR-0007), so a
 *     suspension or termination takes effect in SECONDS — the agent's old token
 *     is refused by firestore.rules rather than left to expire.
 *   - Mark-KYC-verified action: stamps verifiedAt + verifierUid on the kyc
 *     subfield. (Document upload still ships later.)
 *
 * Writes go straight to Firestore via `agentsRepo.update(...)` — firestore.rules
 * already gate the collection to super-admin only, and claimsSyncAgent
 * re-derives the agent's claim from each write.
 */
export function SuperadminAgentsPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const t = useI18n();

  const isSuperAdmin = isSuperAdminForBrand(claims, brand.id);

  const subscribe = useMemo(() => {
    if (!isSuperAdmin) return null;
    return (cb: (vs: AgentProfile[]) => void) => subscribeAgentsForBrand(brand.id, cb);
  }, [isSuperAdmin, brand.id]);

  const {
    data: agents,
    loading,
    error,
  } = useTrackedSnapshot<AgentProfile[]>(['superadmin-agents', brand.id], subscribe);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AgentStatus | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (authLoading) {
    return <Screen title="Signing you in…" body="One second." />;
  }
  if (!user) {
    return (
      <Screen
        title="You're signed out."
        body="Sign in with a super-admin account for this brand to view the agents queue."
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }
  if (!isSuperAdmin) {
    return (
      <Screen
        title="You're not a super-admin here."
        body={`Your account isn't a super-admin for ${brand.name}. If that's wrong, ask the platform owner to grant access.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  // Scope agents to the current platform brand (ADR-0017 isolation).
  const list = (agents ?? []).filter((a) => a.platformBrandId === brand.id);
  const filtered = filterAgents(list, query, statusFilter);
  const selected = selectedId ? (list.find((a) => a.id === selectedId) ?? null) : null;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              letterSpacing: 4,
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {brand.name} · Super-admin
          </p>
          <h1
            style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}
          >
            Agents
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {loading
              ? t.t('common.loading')
              : `${list.length} on this brand · ${filtered.length} shown`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <ProcessOutletRemovalsButton />
          <Link to="/queue" style={ghostLinkStyle}>
            Back to queue
          </Link>
        </div>
      </header>

      <div className="layout-split-aside">
        <Surface as="section">
          <Filters
            query={query}
            onQueryChange={setQuery}
            status={statusFilter}
            onStatusChange={setStatusFilter}
          />

          <div style={{ marginTop: 'var(--space-4)' }}>
            {loading ? (
              <InlineNote>Loading agents… (live)</InlineNote>
            ) : error ? (
              <InlineNote tone="error">
                Couldn't load agents: {String(error.message ?? error)}. Refresh to retry.
              </InlineNote>
            ) : list.length === 0 ? (
              <EmptyState
                title="No agents on this brand yet"
                body={`When someone signs up at /become-an-agent on ${brand.name}, they'll appear here automatically.`}
              />
            ) : filtered.length === 0 ? (
              <EmptyState
                title="No agents match your filters"
                body="Try a different search or set the status filter to All."
                cta={{
                  label: 'Clear filters',
                  onClick: () => {
                    setQuery('');
                    setStatusFilter('all');
                  },
                }}
              />
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'grid',
                  gap: 'var(--space-2)',
                }}
              >
                {filtered.map((a) => (
                  <li key={a.id}>
                    <AgentRow
                      agent={a}
                      selected={a.id === selectedId}
                      onSelect={() => setSelectedId(a.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Surface>

        <section style={{ ...cardStyle, position: 'sticky', top: 'var(--space-4)' }}>
          {selected ? (
            <AgentDetail agent={selected} currentUserUid={user.uid} brandName={brand.name} />
          ) : (
            <EmptyState
              title="Select an agent"
              body="Pick someone on the left to view their profile, change status, set tier, or mark KYC verified."
            />
          )}
        </section>
      </div>
    </main>
  );
}

// ── filters ────────────────────────────────────────────────────────────

function Filters({
  query,
  onQueryChange,
  status,
  onStatusChange,
}: {
  readonly query: string;
  readonly onQueryChange: (s: string) => void;
  readonly status: AgentStatus | 'all';
  readonly onStatusChange: (s: AgentStatus | 'all') => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={fieldLabel}>Search by name, code, or email</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Aiman / EN-A2BC3 / aiman@…"
          autoComplete="off"
          style={inputStyle}
        />
      </label>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>Status</legend>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          {(['all', 'active', 'pending_kyc', 'suspended', 'terminated', 'applied'] as const).map(
            (s) => (
              <button
                key={s}
                type="button"
                aria-pressed={status === s}
                onClick={() => onStatusChange(s)}
                style={chipStyle(status === s)}
              >
                {s === 'all' ? 'All' : labelForStatus(s as AgentStatus)}
              </button>
            ),
          )}
        </div>
      </fieldset>
    </div>
  );
}

// ── list row ────────────────────────────────────────────────────────────

function AgentRow({
  agent,
  selected,
  onSelect,
}: {
  readonly agent: AgentProfile;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  const pill = pillForStatus(agent.status);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: '100%',
        textAlign: 'left',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: selected ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
        background: selected ? 'rgba(13,122,79,0.04)' : 'var(--color-surface)',
        cursor: 'pointer',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 500 }}>{agent.name}</span>
          <span
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            {agent.code}
          </span>
        </div>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {agent.email} · {agent.phone} · Tier {agent.tier}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span
          style={{
            background: pill.bg,
            color: pill.fg,
            padding: '4px 10px',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {pill.label}
        </span>
      </div>
    </button>
  );
}

// ── detail panel ───────────────────────────────────────────────────────

function AgentDetail({
  agent,
  currentUserUid,
  brandName,
}: {
  readonly agent: AgentProfile;
  readonly currentUserUid: string;
  readonly brandName: string;
}) {
  const [savingField, setSavingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Live subscribe to back-references + ledger summary
  const operatorsSub = useMemo(
    () => (cb: (vs: AgentOperatorBackref[]) => void) =>
      agentOperatorsRepoFor(agent.id).listSnapshot(cb),
    [agent.id],
  );
  const { data: operators } = useTrackedSnapshot<AgentOperatorBackref[]>(
    ['agent-operators', agent.id],
    operatorsSub,
  );

  const ledgerSub = useMemo(
    () => (cb: (vs: EarningsLedgerEntry[]) => void) =>
      agentEarningsLedgerRepoFor(agent.id).listSnapshot(cb),
    [agent.id],
  );
  const { data: ledger } = useTrackedSnapshot<EarningsLedgerEntry[]>(
    ['agent-ledger', agent.id],
    ledgerSub,
  );

  async function updateField(
    fieldName: string,
    patch: Record<string, unknown>,
    successMsg: string,
  ) {
    setSavingField(fieldName);
    setError(null);
    setNotice(null);
    try {
      await agentsRepo.update(agent.id, {
        ...patch,
        updatedAt: Timestamp.now(),
      });
      setNotice(successMsg);
    } catch (e) {
      logger.error('agent update failed', { field: fieldName, error: String(e) });
      setError(String((e as Error).message ?? 'Could not save.'));
    } finally {
      setSavingField(null);
    }
  }

  function setStatus(next: AgentStatus) {
    if (next === agent.status) return;
    const friendlyEffect = (() => {
      switch (next) {
        case 'active':
          return 'Agent claims are restored immediately — their app picks it up within seconds. They can sign back in right away.';
        case 'pending_kyc':
          return 'Agent still has app access; surfaces will nudge them to finish KYC.';
        case 'suspended':
          return 'Agent loses access immediately — their token is revoked server-side, not left to expire. Commission accrual pauses too. They keep their code; reactivating restores everything.';
        case 'terminated':
          return 'Agent loses access on next refresh. Their code stays on file for historical attribution but no new commissions accrue. Operators they brought in are NOT affected.';
        case 'applied':
          return 'Reverts them to the application queue (rare; usually for KYC re-review).';
      }
    })();
    void updateField(
      'status',
      { status: next },
      `Status set to ${labelForStatus(next)}. ${friendlyEffect}`,
    );
  }

  function setTier(next: AgentTier) {
    if (next === agent.tier) return;
    void updateField(
      'tier',
      { tier: next },
      `Tier set to ${next}. Future commission accruals use the new multiplier.`,
    );
  }

  function markKycVerified() {
    void updateField(
      'kyc',
      {
        'kyc.verifiedAt': Timestamp.now(),
        'kyc.verifierUid': currentUserUid,
      },
      'KYC marked verified. Payouts above threshold are now allowed.',
    );
  }

  // Unpaid balance mirrors what the monthly payout job will actually pay
  // against — payableBalanceSen shares computePayoutBatch's eligibility
  // (unswept, non-paid, accrued-by-now) so clawbacks reduce it correctly.
  // Date.now() is a live clock read for a display filter, not a stored value.
  const balanceSen = payableBalanceSen(ledger ?? [], Date.now());
  // paid-out debits are stored NEGATIVE; paidOutSen returns the POSITIVE sum.
  const paidOutTotal = paidOutSen(ledger ?? []);

  const operatorList = (operators ?? [])
    .slice()
    .sort((a, b) => tsMs(b.signupAt) - tsMs(a.signupAt));

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <header>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          Agent on {brandName}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'baseline',
            marginTop: 'var(--space-1)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, margin: 0 }}>{agent.name}</h2>
          <span
            style={{
              fontSize: 'var(--text-body)',
              color: 'var(--color-on-surface-muted)',
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            {agent.code}
          </span>
        </div>
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {agent.email} · {agent.phone} · joined {formatDate(tsMs(agent.createdAt))}
        </p>
      </header>

      {/* Status changer */}
      <section style={controlSectionStyle}>
        <h3 style={controlHeadingStyle}>Status</h3>
        <p style={controlHintStyle}>
          {labelForStatus(agent.status)} currently. Changes reach the agent's app within seconds —
          their token is re-issued server-side, not left to expire.
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-2)',
          }}
        >
          {(['active', 'pending_kyc', 'suspended', 'terminated', 'applied'] as const).map((s) => (
            <button
              key={s}
              type="button"
              disabled={savingField !== null}
              onClick={() => setStatus(s)}
              aria-pressed={agent.status === s}
              style={chipStyle(agent.status === s, accentForStatus(s))}
            >
              {labelForStatus(s)}
            </button>
          ))}
        </div>
      </section>

      {/* Tier changer */}
      <section style={controlSectionStyle}>
        <h3 style={controlHeadingStyle}>Tier</h3>
        <p style={controlHintStyle}>
          Bronze 1.0× · Silver 1.1× · Gold 1.25× commission multiplier. Applies to future accruals
          only.
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-2)',
          }}
        >
          {(['bronze', 'silver', 'gold'] as const).map((s) => (
            <button
              key={s}
              type="button"
              disabled={savingField !== null}
              onClick={() => setTier(s)}
              aria-pressed={agent.tier === s}
              style={chipStyle(agent.tier === s)}
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      {/* KYC */}
      <section style={controlSectionStyle}>
        <h3 style={controlHeadingStyle}>KYC</h3>
        {agent.kyc?.verifiedAt ? (
          <>
            <p style={controlHintStyle}>
              Verified on {formatDate(tsMs(agent.kyc.verifiedAt))} by {agent.kyc.verifierUid ?? '—'}
              . Payouts above threshold ({money.format(agent.payout.thresholdSen)}) are allowed.
            </p>
            {agent.kyc.icScanRef ? (
              <div style={{ marginTop: 'var(--space-2)' }}>
                <KycScanLink storagePath={agent.kyc.icScanRef} />
              </div>
            ) : null}
          </>
        ) : agent.kyc?.icScanRef ? (
          <>
            <p style={controlHintStyle}>
              Document submitted
              {agent.kyc.submittedAt ? ` ${formatDate(tsMs(agent.kyc.submittedAt))}` : ''}. Review
              the scan, then mark verified.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
                marginTop: 'var(--space-2)',
              }}
            >
              <KycScanLink storagePath={agent.kyc.icScanRef} />
              <button
                type="button"
                disabled={savingField !== null}
                onClick={markKycVerified}
                style={primaryButtonStyle}
              >
                {savingField === 'kyc' ? 'Saving…' : 'Mark KYC verified'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={controlHintStyle}>
              No document on file yet. The agent uploads from their /profile page; meanwhile you can
              still verify out-of-band (WA) and mark verified once you've sighted IC / passport.
            </p>
            <button
              type="button"
              disabled={savingField !== null}
              onClick={markKycVerified}
              style={primaryButtonStyle}
            >
              {savingField === 'kyc' ? 'Saving…' : 'Mark KYC verified'}
            </button>
          </>
        )}
      </section>

      {/* Operators they brought in */}
      <section style={controlSectionStyle}>
        <h3 style={controlHeadingStyle}>Operators introduced ({operatorList.length})</h3>
        {operatorList.length === 0 ? (
          <p style={controlHintStyle}>
            None yet. When this agent's code is used at signup, the tenant appears here.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {operatorList.slice(0, 5).map((op) => {
              return (
                <li
                  key={op.id}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--tint-subtle)',
                    fontSize: 'var(--text-meta)',
                  }}
                >
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>
                    {op.tenantBusinessName ?? op.id}
                  </span>
                  {op.currentStatus ? ` · ${op.currentStatus}` : ''} · joined{' '}
                  {formatDate(tsMs(op.signupAt))}
                  {op.firstPaidAt
                    ? ` · first paid ${formatDate(tsMs(op.firstPaidAt))}`
                    : ' · never paid'}
                </li>
              );
            })}
            {operatorList.length > 5 ? (
              <li style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
                + {operatorList.length - 5} more
              </li>
            ) : null}
          </ul>
        )}
      </section>

      {/* Payout details editor (Sprint 23) */}
      <PayoutDetailsSection
        agent={agent}
        onSave={async (next, successMsg) => {
          // Spread the full payout object: Firestore wouldn't accept a
          // partial nested write without using dotted field paths, and this
          // section presents the whole bank/DuitNow block at once anyway.
          await updateField('payout', { payout: next }, successMsg);
        }}
        saving={savingField === 'payout'}
      />

      {/* Earnings summary */}
      <section style={controlSectionStyle}>
        <h3 style={controlHeadingStyle}>Earnings</h3>
        <dl style={statGrid}>
          <Stat label="Unpaid balance" value={money.format(balanceSen)} />
          <Stat label="Paid out (lifetime)" value={money.format(paidOutTotal)} />
          <Stat label="Ledger entries" value={String((ledger ?? []).length)} />
          <Stat label="Payout threshold" value={money.format(agent.payout.thresholdSen)} />
        </dl>
        {!agent.payout.bankName && !agent.payout.duitNowId ? (
          <p
            role="alert"
            style={{
              margin: 'var(--space-3) 0 0 0',
              padding: 'var(--space-2) var(--space-3)',
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.24)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface)',
            }}
          >
            No payout details on file. Agent needs to add bank or DuitNow before any monthly payout
            can run. Use the form above to add them.
          </p>
        ) : null}
      </section>

      {error ? (
        <p
          role="alert"
          style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
        >
          {error}
        </p>
      ) : null}
      {notice ? (
        <p
          role="status"
          style={{ margin: 0, color: 'var(--color-primary)', fontSize: 'var(--text-meta)' }}
        >
          {notice}
        </p>
      ) : null}
    </div>
  );
}

// ── billing manual trigger ─────────────────────────────────────────────

/**
 * Header-button surface for the Sprint 24 `processOutletRemovalsNow`
 * callable. The scheduled `outletRemovalsDaily` job runs at 00:30 SGT
 * nightly; this button lets a super-admin force-run it on demand for:
 *   - QA/testing the archival pipeline before production
 *   - urgent single-tenant cleanup ("we just removed an outlet, archive now")
 *   - dev/local where the scheduler isn't firing
 *
 * Idempotent — re-running yields zero archivals once everything's caught up.
 * Surfaces every state explicitly per feedback-no-dead-ends.
 */
function ProcessOutletRemovalsButton() {
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'running' }
    | { kind: 'done'; archived: number; skipped: number; tenants: number }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function run() {
    setStage({ kind: 'running' });
    try {
      const result = await callProcessOutletRemovalsNow();
      setStage({
        kind: 'done',
        archived: result.archivedCount,
        skipped: result.skippedCount,
        tenants: result.tenantsScanned,
      });
      if (result.skippedCount > 0) {
        logger.warn('processOutletRemovals: some archivals skipped', { skipped: result.skipped });
      }
    } catch (e) {
      logger.error('processOutletRemovalsNow failed', { error: String(e) });
      setStage({ kind: 'error', message: String((e as Error).message ?? 'Run failed.') });
    }
  }

  const label = (() => {
    switch (stage.kind) {
      case 'idle':
        return 'Run pending archivals';
      case 'running':
        return 'Running…';
      case 'done':
        if (stage.archived === 0)
          return `Caught up · ${stage.tenants} tenant${stage.tenants === 1 ? '' : 's'} scanned`;
        return `Archived ${stage.archived}${stage.skipped > 0 ? ` · ${stage.skipped} skipped` : ''}`;
      case 'error':
        return 'Run failed — tap to retry';
    }
  })();

  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={stage.kind === 'running'}
      title="Force-run the daily outlet-archival job (normally fires at 00:30 SGT)"
      style={{
        ...miniGhostButtonStyle,
        ...(stage.kind === 'done'
          ? { borderColor: 'rgba(13,122,79,0.32)', color: 'var(--color-primary)' }
          : {}),
        ...(stage.kind === 'error'
          ? { borderColor: 'rgba(239,68,68,0.32)', color: 'var(--color-accent)' }
          : {}),
        ...(stage.kind === 'running' ? { opacity: 0.6, cursor: 'wait' } : {}),
      }}
    >
      {label}
    </button>
  );
}

// ── payout details ────────────────────────────────────────────────────

function PayoutDetailsSection({
  agent,
  onSave,
  saving,
}: {
  readonly agent: AgentProfile;
  readonly onSave: (next: AgentProfile['payout'], successMsg: string) => Promise<void>;
  readonly saving: boolean;
}) {
  const [bankName, setBankName] = useState(agent.payout.bankName ?? '');
  const [accountName, setAccountName] = useState(agent.payout.accountName ?? '');
  const [accountNumber, setAccountNumber] = useState(agent.payout.accountNumber ?? '');
  const [duitNowId, setDuitNowId] = useState(agent.payout.duitNowId ?? '');
  const [thresholdRm, setThresholdRm] = useState(
    String((agent.payout.thresholdSen ?? 10000) / 100),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  // Re-hydrate when a different agent is selected.
  useEffect(() => {
    setBankName(agent.payout.bankName ?? '');
    setAccountName(agent.payout.accountName ?? '');
    setAccountNumber(agent.payout.accountNumber ?? '');
    setDuitNowId(agent.payout.duitNowId ?? '');
    setThresholdRm(String((agent.payout.thresholdSen ?? 10000) / 100));
    setLocalError(null);
  }, [agent.id, agent.payout]);

  const dirty =
    bankName !== (agent.payout.bankName ?? '') ||
    accountName !== (agent.payout.accountName ?? '') ||
    accountNumber !== (agent.payout.accountNumber ?? '') ||
    duitNowId !== (agent.payout.duitNowId ?? '') ||
    String((agent.payout.thresholdSen ?? 10000) / 100) !== thresholdRm;

  const hasAnyDestination = bankName.trim() !== '' || duitNowId.trim() !== '';

  async function handleSave() {
    setLocalError(null);
    const thresholdNum = Number(thresholdRm);
    if (!Number.isFinite(thresholdNum) || thresholdNum < 0) {
      setLocalError('Threshold must be a non-negative number (RM).');
      return;
    }
    // Server requires at least one destination once any payout is attempted —
    // for MVP we accept saving with nothing, with the warning in the Earnings
    // section. Bank details require BOTH account name + number when bankName set.
    if (bankName.trim() && (!accountName.trim() || !accountNumber.trim())) {
      setLocalError('Bank account needs both account name and account number.');
      return;
    }
    const next: AgentProfile['payout'] = {
      ...(bankName.trim() ? { bankName: bankName.trim() } : {}),
      ...(accountName.trim() ? { accountName: accountName.trim() } : {}),
      ...(accountNumber.trim() ? { accountNumber: accountNumber.trim() } : {}),
      ...(duitNowId.trim() ? { duitNowId: duitNowId.trim() } : {}),
      thresholdSen: Math.max(0, Math.round(thresholdNum * 100)),
    };
    await onSave(
      next,
      hasAnyDestination
        ? 'Payout details saved. Monthly payout will use these on the next run.'
        : 'Saved threshold. Add a bank or DuitNow destination before the next payout run.',
    );
  }

  function handleReset() {
    setBankName(agent.payout.bankName ?? '');
    setAccountName(agent.payout.accountName ?? '');
    setAccountNumber(agent.payout.accountNumber ?? '');
    setDuitNowId(agent.payout.duitNowId ?? '');
    setThresholdRm(String((agent.payout.thresholdSen ?? 10000) / 100));
    setLocalError(null);
  }

  return (
    <section style={controlSectionStyle}>
      <h3 style={controlHeadingStyle}>Payout details</h3>
      <p style={controlHintStyle}>
        Either fill bank details (bank + account name + account number) or set a DuitNow ID (NRIC or
        mobile). Threshold is the minimum unpaid-balance the monthly payout job pays out; below it,
        balance rolls over.
      </p>

      <div
        style={{
          marginTop: 'var(--space-3)',
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
        }}
      >
        <PayoutField
          label="Bank name"
          value={bankName}
          onChange={setBankName}
          placeholder="e.g. Maybank"
        />
        <PayoutField
          label="Account name"
          value={accountName}
          onChange={setAccountName}
          placeholder="As per bank record"
        />
        <PayoutField
          label="Account number"
          value={accountNumber}
          onChange={setAccountNumber}
          placeholder="Digits only"
          inputMode="numeric"
        />
        <PayoutField
          label="DuitNow ID"
          value={duitNowId}
          onChange={setDuitNowId}
          placeholder="NRIC or +60 mobile"
        />
        <PayoutField
          label="Threshold (RM)"
          value={thresholdRm}
          onChange={setThresholdRm}
          inputMode="decimal"
          tabularNums
        />
      </div>

      {localError ? (
        <p
          role="alert"
          style={{
            margin: 'var(--space-3) 0 0 0',
            color: 'var(--color-accent)',
            fontSize: 'var(--text-meta)',
          }}
        >
          {localError}
        </p>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          justifyContent: 'flex-end',
          marginTop: 'var(--space-3)',
        }}
      >
        {dirty ? (
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            style={miniGhostButtonStyle}
          >
            Reset
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || saving}
          style={{
            ...primaryButtonStyle,
            padding: '8px 14px',
            opacity: !dirty || saving ? 0.5 : 1,
            cursor: !dirty || saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : dirty ? 'Save payout details' : 'No changes'}
        </button>
      </div>
    </section>
  );
}

function PayoutField({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  tabularNums,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (s: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: 'numeric' | 'decimal' | 'tel' | 'text';
  readonly tabularNums?: boolean;
}) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span style={fieldLabel}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        style={{ ...inputStyle, ...(tabularNums ? { fontVariantNumeric: 'tabular-nums' } : {}) }}
      />
    </label>
  );
}

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '8px 12px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};

// ── helpers ────────────────────────────────────────────────────────────

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

function filterAgents(
  list: readonly AgentProfile[],
  rawQuery: string,
  statusFilter: AgentStatus | 'all',
): AgentProfile[] {
  const q = rawQuery.trim().toLowerCase();
  return list.filter((a) => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (!q) return true;
    return (
      a.name.toLowerCase().includes(q) ||
      a.code.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q)
    );
  });
}

function pillForStatus(s: AgentStatus): { bg: string; fg: string; label: string } {
  switch (s) {
    case 'active':
      return { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)', label: 'Active' };
    case 'pending_kyc':
      return { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', label: 'Pending KYC' };
    case 'suspended':
      return { bg: 'rgba(239,68,68,0.10)', fg: 'var(--color-accent)', label: 'Suspended' };
    case 'terminated':
      return {
        bg: 'var(--color-border)',
        fg: 'var(--color-on-surface-muted)',
        label: 'Terminated',
      };
    case 'applied':
      return { bg: 'var(--tint-subtle)', fg: 'var(--color-on-surface-muted)', label: 'Applied' };
  }
}

function accentForStatus(s: AgentStatus): string | undefined {
  if (s === 'suspended' || s === 'terminated') return 'var(--color-accent)';
  return undefined;
}

function labelForStatus(s: AgentStatus): string {
  switch (s) {
    case 'active':
      return 'Active';
    case 'pending_kyc':
      return 'Pending KYC';
    case 'suspended':
      return 'Suspended';
    case 'terminated':
      return 'Terminated';
    case 'applied':
      return 'Applied';
  }
}

function tsMs(ts: { toMillis?: () => number; seconds: number } | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: 'var(--text-body)',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function EmptyState({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; onClick?: () => void };
}) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: 'var(--space-6) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
        justifyItems: 'center',
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 360,
        }}
      >
        {body}
      </p>
      {cta?.onClick ? (
        <button type="button" onClick={cta.onClick} style={ghostButtonStyle}>
          {cta.label}
        </button>
      ) : null}
    </div>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: 'var(--space-3) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 380 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const controlSectionStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
};

const controlHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};

const controlHintStyle: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const statGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-3) var(--space-4)',
  margin: 'var(--space-3) 0 0 0',
};

function chipStyle(selected: boolean, accent?: string): React.CSSProperties {
  const ring = accent ?? 'var(--color-primary)';
  return {
    padding: '8px 14px',
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${selected ? ring : 'var(--color-border-strong)'}`,
    background: selected ? (accent ?? 'var(--color-primary)') : 'transparent',
    color: selected ? (accent ? 'white' : 'var(--color-primary-fg)') : 'var(--color-on-surface)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    cursor: 'pointer',
  };
}
